import React, { useState } from 'react';
import styled from 'astroturf/react';

import { Button } from 'js/design_system/Button';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import BookmarkIcon from 'js/design_system/icons/bookmark.svg';
import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import BookmarkOutlineIcon from 'js/design_system/icons/bookmark_outline.svg';
import StarIcon from 'js/svg/colorless/stars.svg';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';

import { Paper, OpenalexWork, AiButtonType } from 'js/social/shared/types';

import CitationModal from 'js/journals/CitationModal';
import _ButtonsContainer from "PaperInteractions-ButtonsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperInteractions.tsx?styleId=ButtonsContainer";


const ButtonsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonsContainer",
  styles: _ButtonsContainer
});

type Props = {
  work: Paper | OpenalexWork;
  aiButton?: AiButtonType;
  showBookmark: boolean;
  canUseReader: boolean;
  showCiteButton?: boolean;
  isBookmarked: boolean;
  isSnippet?: boolean;
  className?: string;
  onBookmark?: () => void;
  onUnbookmark?: () => void;
  onExplain?: () => void;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
};

const PaperInteractions: React.FunctionComponent<Props> = ({
  work,
  showBookmark = false,
  aiButton,
  showCiteButton = false,
  onExplain = () => null,
  canUseReader,
  isBookmarked: initialIsBookmarked,
  impressionPropsGen,
  onBookmark = () => null,
  onUnbookmark = () => null,
  showReader,
}) => {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked);
  const [isLoading, setIsLoading] = useState(false);

  const handleBookmark = async () => {
    setIsBookmarked((prev) => !prev);
    setIsLoading(true);

    try {
      isBookmarked ? await onUnbookmark() : await onBookmark();
    } catch (error) {
      console.error('Failed to update bookmark:', error);
      setIsBookmarked((prev) => !prev);
    } finally {
      setIsLoading(false);
    }
  };

  const [citationModalOpen, setCitationModalOpen] = useState(false);
  const openCitationModal = () => {
    setCitationModalOpen(true);
  };
  const closeCitationModal = () => {
    setCitationModalOpen(false);
  };

  const breakpoint = useBreakpoint();
  const viewPaperCopy =
    breakpoint === 'small' ? 'View Paper' : 'View Full Paper';
  const aiButtonText = breakpoint === 'small' ? 'Explain' : 'Explain this';

  return (
    <ButtonsContainer>
      <ImpressionWatcher {...impressionPropsGen('view-button')}>
        {canUseReader ? (
          <Button
            size="xs"
            icon="description"
            disabled={isLoading}
            onClick={showReader}
            data-coachmark-step="view-full-paper"
          >
            {viewPaperCopy}
          </Button>
        ) : (
          <ButtonLink
            size="xs"
            iconRight="arrow_outward"
            href={work.url}
            target={'_blank'}
            data-coachmark-step="view-full-paper"
          >
            {viewPaperCopy}
          </ButtonLink>
        )}
      </ImpressionWatcher>
      {showBookmark && (
        <ImpressionWatcher {...impressionPropsGen('bookmark-button')}>
          <Button
            size="xs"
            icon={
              isBookmarked ? (
                <BookmarkIcon size={20} />
              ) : (
                <BookmarkOutlineIcon size={20} />
              )
            }
            disabled={isLoading}
            onClick={handleBookmark}
          >
            {isBookmarked ? 'Saved' : 'Save'} to library
          </Button>
        </ImpressionWatcher>
      )}
      {aiButton && (
        <ImpressionWatcher {...impressionPropsGen('ai-button')}>
          <Button size="xs" icon={<StarIcon size={20} />} onClick={onExplain}>
            {aiButtonText} {aiButton}
          </Button>
        </ImpressionWatcher>
      )}
      {showCiteButton && work.formattedCitations && (
        <>
          <ImpressionWatcher {...impressionPropsGen('cite-button')}>
            <Button size="xs" icon="format_quote" onClick={openCitationModal}>
              Cite
            </Button>
          </ImpressionWatcher>
          <CitationModal
            open={citationModalOpen}
            citations={work.formattedCitations}
            onClose={closeCitationModal}
            entity_id={work.id}
            source={impressionPropsGen('cite-button').source}
          />
        </>
      )}
    </ButtonsContainer>
  );
};

export default PaperInteractions;
